import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Briefcase, Award, Target, Lightbulb, Shield, Zap } from 'lucide-react';
import { aboutContent, studioInfo, certifications } from '../mock';

const About = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.6, 0.01, 0.05, 0.95]
      }
    }
  };

  const valueIcons = {
    'Precisione': Target,
    'Innovazione': Lightbulb,
    'Affidabilità': Shield
  };

  return (
    <div className="bg-[#0A1628] min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Info */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-[#1A2B47] to-[#0A1628] rounded-3xl p-8 md:p-12 border border-white/5 shadow-2xl">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{studioInfo.name}</h2>
                <p className="text-xl text-[#D4AF37] mb-4">{studioInfo.title}</p>
                <p className="text-gray-400 leading-relaxed max-w-2xl mx-auto">
                  {studioInfo.description}
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 pt-6 border-t border-white/10 max-w-2xl mx-auto">
                <div className="flex items-center space-x-3 justify-center sm:justify-start">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm text-gray-500">P.IVA</p>
                    <p className="text-white font-medium">{studioInfo.piva}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 justify-center sm:justify-start">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm text-gray-500">Timbro Professionale</p>
                    <p className="text-white font-medium">N° {studioInfo.timbro}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Values */}
        <motion.div
          ref={sectionRef}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            I Nostri <span className="text-[#D4AF37]">Valori</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {aboutContent.values.map((value, index) => {
              const IconComponent = valueIcons[value.title];
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="bg-gradient-to-br from-[#1A2B47] to-[#0A1628] rounded-2xl p-8 border border-white/5 shadow-lg hover:border-[#D4AF37]/30 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                    <IconComponent className="w-7 h-7 text-[#0A1628]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Certifications */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Certificazioni e <span className="text-[#D4AF37]">Abilitazioni</span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-br from-[#1A2B47] to-[#0A1628] rounded-2xl p-6 border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-300 shadow-lg"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{cert.title}</h3>
                    <p className="text-sm text-gray-400">{cert.number}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
